import { useIntl } from "react-intl";

import { Radio } from "@/components/Radio/Radio";
import {
  RadioGroup,
  type RadioGroupProps,
} from "@/components/Radio/RadioGroup";
import { getTravellerAgeAtTravelDate } from "@/features/travellers/utils/ageValidation";
import { getTravelerTypeOfAge } from "@/features/travellers/utils/transformTraveler";
import type { Traveller } from "@/types/types";
import { getAgeFromDateOfBirth } from "@/utils/getAgefromDateOfBirth";

type Props = RadioGroupProps & {
  travelDate: Date;
  selectedTravellers: Traveller[];
};

export const MainTravellerRadioButtons = ({
  travelDate,
  selectedTravellers,
  ...props
}: Props) => {
  const intl = useIntl();

  return (
    <RadioGroup {...props}>
      {selectedTravellers.map((traveller) => {
        const ageAtTravelDate = getTravellerAgeAtTravelDate(
          traveller.birthDate,
          travelDate,
        );
        const ageGroupAtTravelDate = getTravelerTypeOfAge(ageAtTravelDate);

        const currentAge = getAgeFromDateOfBirth(traveller.birthDate);
        const currentAgeGroup = getTravelerTypeOfAge(currentAge);

        return (
          <div
            key={traveller.id}
            className="border-b border-stroke-neutral-soft p-2 last:border-b-0"
          >
            <Radio
              value={traveller.id}
              disabled={ageGroupAtTravelDate !== "adult"}
              label={
                <div className="flex w-full items-center gap-2">
                  <div className="grow" data-sentry-mask>
                    <div className="font-medium text-text-title">
                      {traveller.firstName} {traveller.lastName}
                    </div>
                    <div className="text-xs text-text-tertiary">
                      {intl.formatMessage({
                        defaultMessage: "Age",
                        description:
                          "Label for minimum check-in age requirement on the hotel details page",
                      })}{" "}
                      {currentAge}
                    </div>
                    {ageGroupAtTravelDate !== currentAgeGroup ? (
                      <div className="mt-1 text-xs text-semantic-warning-strong">
                        {intl.formatMessage(
                          {
                            defaultMessage:
                              "Traveller will be {age} years old at travel date, which makes them counted as {ageGroupAtTravelDate, select, child {a Child} infant {an Infant} other {an Adult}}",
                            description:
                              "Error shown when a child traveller's age is not valid for the selected booking",
                          },
                          {
                            ageGroupAtTravelDate,
                            age: ageAtTravelDate,
                          },
                        )}
                      </div>
                    ) : null}
                  </div>
                  {props.value === traveller.id ? (
                    <div className="shrink-0 text-xs font-medium">
                      {intl.formatMessage({
                        defaultMessage: "Main traveller",
                        description:
                          "Badge or label identifying the primary / main traveller in a booking",
                      })}
                    </div>
                  ) : null}
                </div>
              }
            />
          </div>
        );
      })}
    </RadioGroup>
  );
};
